import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { DatePipe, DecimalPipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, debounceTime, distinctUntilChanged, tap } from 'rxjs';

import { EmailQueueService } from '../../../core/admin/email-queue.service';
import { ApproveResult, EmailQueueView, HeldEmailRow } from '../../../core/admin/email-queue.types';
import {
  ConfirmDialogComponent,
  ConfirmDialogData,
} from '../../../shared/confirm-dialog/confirm-dialog';
import { IconComponent } from '../../../shared/icon/icon';
import { StatusChipComponent } from '../../../shared/status-chip/status-chip';
import { EmailPreviewComponent } from './email-preview';

const plural = (n: number) => `${n} ${n === 1 ? 'email' : 'emails'}`;

/** Email to non-staff waits here until an admin sends or deletes it. */
@Component({
  selector: 'app-email-approvals',
  imports: [
    DatePipe,
    DecimalPipe,
    RouterLink,
    MatButtonModule,
    MatButtonToggleModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatTooltipModule,
    IconComponent,
    StatusChipComponent,
  ],
  templateUrl: './email-approvals.html',
  styleUrl: './email-approvals.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmailApprovalsComponent implements OnInit {
  private readonly service = inject(EmailQueueService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly destroyRef = inject(DestroyRef);
  private readonly searches = new Subject<string>();

  protected readonly views: { value: EmailQueueView; label: string }[] = [
    { value: 'HELD', label: 'Waiting' },
    { value: 'FAILED', label: 'Failed' },
    { value: 'SENT', label: 'Sent' },
    { value: 'DELETED', label: 'Deleted' },
  ];
  protected readonly pageSizes = [25, 50, 100];

  protected readonly rows = signal<HeldEmailRow[]>([]);
  protected readonly counts = signal<Record<EmailQueueView, number> | null>(null);
  protected readonly categories = signal<{ value: string; label: string }[]>([]);
  protected readonly loading = signal(true);
  protected readonly error = signal(false);

  protected readonly view = signal<EmailQueueView>('HELD');
  protected readonly search = signal('');
  protected readonly category = signal('');
  protected readonly page = signal(1);
  protected readonly pageSize = signal(25);
  protected readonly total = signal(0);
  protected readonly hasNext = signal(false);

  protected readonly selected = signal<ReadonlySet<string>>(new Set());

  /** A sent email is on the record and has nothing left to decide. */
  protected readonly selectable = computed(() => this.view() !== 'SENT');
  protected readonly canSend = computed(() => this.view() === 'HELD' || this.view() === 'FAILED');
  protected readonly allSelected = computed(
    () => this.rows().length > 0 && this.selected().size === this.rows().length,
  );
  protected readonly filtered = computed(() => !!this.search().trim() || !!this.category());
  protected readonly showPager = computed(
    () => this.hasNext() || this.page() > 1 || this.total() > 25,
  );
  protected readonly rangeStart = computed(() =>
    this.total() === 0 ? 0 : (this.page() - 1) * this.pageSize() + 1,
  );
  protected readonly rangeEnd = computed(() =>
    Math.min(this.page() * this.pageSize(), this.total()),
  );

  ngOnInit(): void {
    this.searches
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.search.set(value);
        this.page.set(1);
        this.load();
      });
    this.load();
  }

  protected onSearch(value: string): void {
    this.searches.next(value);
  }

  protected setView(view: EmailQueueView): void {
    this.view.set(view);
    this.page.set(1);
    this.load();
  }

  protected setCategory(value: string): void {
    this.category.set(value);
    this.page.set(1);
    this.load();
  }

  protected setPageSize(size: number): void {
    this.pageSize.set(size);
    this.page.set(1);
    this.load();
  }

  protected clearFilters(): void {
    this.search.set('');
    this.searches.next('');
    this.category.set('');
    this.page.set(1);
    this.load();
  }

  protected goTo(page: number): void {
    this.page.set(page);
    this.load();
  }

  protected toggle(id: string, checked: boolean): void {
    const next = new Set(this.selected());
    if (checked) next.add(id);
    else next.delete(id);
    this.selected.set(next);
  }

  protected toggleAll(checked: boolean): void {
    this.selected.set(new Set(checked ? this.rows().map((r) => r.id) : []));
  }

  protected preview(row: HeldEmailRow): void {
    this.dialog.open(EmailPreviewComponent, { data: row.id, width: '720px' });
  }

  protected approve(ids: string[]): void {
    this.ask({
      title: `Send ${plural(ids.length)}?`,
      message:
        'They go to the recipients now and cannot be called back. Any sign-in link restarts its clock when it is sent.',
      confirmLabel: 'Send',
      confirm: () => this.service.approve(ids).pipe(tap((r) => this.reportSent(r))),
    });
  }

  protected remove(ids: string[]): void {
    this.ask({
      title: `Delete ${plural(ids.length)}?`,
      message: 'They will not be sent. You can restore them from Deleted.',
      confirmLabel: 'Delete',
      destructive: true,
      confirm: () =>
        this.service.delete(ids).pipe(tap((r) => this.toast(`${plural(r.deleted)} deleted.`))),
    });
  }

  protected restore(ids: string[]): void {
    this.service
      .restore(ids)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (r) => {
          this.toast(`${plural(r.restored)} restored.`);
          this.load();
        },
        error: () => this.toast("Couldn't restore. Try again."),
      });
  }

  protected selectedIds(): string[] {
    return [...this.selected()];
  }

  protected load(): void {
    this.loading.set(true);
    this.error.set(false);
    this.selected.set(new Set());
    this.service
      .list({
        view: this.view(),
        q: this.search().trim() || undefined,
        category: this.category() || undefined,
        page: this.page(),
        page_size: this.pageSize(),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.rows.set(res.results);
          this.counts.set(res.counts);
          this.categories.set(res.categories);
          this.total.set(res.total);
          this.hasNext.set(res.has_next);
          this.loading.set(false);
        },
        error: () => {
          this.error.set(true);
          this.loading.set(false);
        },
      });
  }

  private ask(data: ConfirmDialogData): void {
    this.dialog
      .open(ConfirmDialogComponent, { width: '440px', data })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((done) => {
        if (done) this.load();
      });
  }

  private reportSent(r: ApproveResult): void {
    const parts = [`${plural(r.sent)} sent`];
    if (r.failed) parts.push(`${r.failed} failed — see Failed`);
    if (r.skipped) parts.push(`${r.skipped} already dealt with`);
    this.toast(`${parts.join(', ')}.`);
  }

  private toast(message: string): void {
    this.snackBar.open(message, undefined, { duration: 5000 });
  }
}
